import type { Account, UserSettings } from '@/api';
import { isLostBonusEnabled } from '@/domain/lost-bonuses';
import type { IconName } from '@/ui/common';

export type ProfileNavId =
  | 'details'
  | 'tickets'
  | 'transactions'
  | 'deposits'
  | 'withdrawals'
  | 'bonuses'
  | 'claim-bonus'
  | 'settings'
  | 'messages'
  | 'kyc'
  | 'deposit-limits'
  | 'self-exclusion';

export interface ProfileNavItem {
  id: ProfileNavId;
  label: string;
  to:
    | '/profile/details'
    | '/profile/tickets'
    | '/profile/transactions'
    | '/profile/deposits'
    | '/profile/withdrawals'
    | '/profile/bonuses'
    | '/profile/claimbonus'
    | '/profile/settings'
    | '/profile/messages'
    | '/profile/kyc'
    | '/profile/deposit-limits'
    | '/profile/self-exclusion';
  icon: IconName;
  /** When set, item is shown only if the flag is true (legacy CheckSettings / modules). */
  require?: (
    account: Account | null | undefined,
    user?: UserSettings | null,
  ) => boolean;
}

const PROFILE_NAV_ITEMS: ProfileNavItem[] = [
  { id: 'details', label: 'Details', to: '/profile/details', icon: 'details' },
  { id: 'tickets', label: 'My Bets', to: '/profile/tickets', icon: 'mytickets' },
  { id: 'transactions', label: 'Transactions', to: '/profile/transactions', icon: 'transactions' },
  {
    id: 'deposits',
    label: 'Deposits',
    to: '/profile/deposits',
    icon: 'deposit',
    require: (account) => account?.AllowDeposits !== false,
  },
  {
    id: 'withdrawals',
    label: 'Withdrawals',
    to: '/profile/withdrawals',
    icon: 'withdrawal',
    require: (account) => account?.AllowWithdrawals !== false,
  },
  { id: 'bonuses', label: 'Bonuses', to: '/profile/bonuses', icon: 'bonuses' },
  {
    id: 'claim-bonus',
    label: 'Claim bonus',
    to: '/profile/claimbonus',
    icon: 'bonus-gift',
    require: (account, user) => isLostBonusEnabled(account, user),
  },
  { id: 'settings', label: 'Settings', to: '/profile/settings', icon: 'settings' },
  { id: 'messages', label: 'Messages', to: '/profile/messages', icon: 'messages' },
  {
    id: 'kyc',
    label: 'KYC',
    to: '/profile/kyc',
    icon: 'lock',
    require: (account) => account?.AllowUserRegistration !== false,
  },
  {
    id: 'deposit-limits',
    label: 'Deposit Limits',
    to: '/profile/deposit-limits',
    icon: 'filters',
    require: (account) => account?.AllowDepositSoftLimits === true,
  },
  {
    id: 'self-exclusion',
    label: 'Self-Exclusion',
    to: '/profile/self-exclusion',
    icon: 'warning',
    require: (account) => account?.AllowSelfExclusion === true,
  },
];

/** Primary items shown first in the header account dropdown (collapsed). */
export const PROFILE_MENU_PRIMARY_IDS: readonly ProfileNavId[] = [
  'tickets',
  'transactions',
  'bonuses',
];

/** Account-flag-gated profile menu items (legacy CheckSettings / modules). */
export function profileNavItems(
  account: Account | null | undefined,
  user?: UserSettings | null,
): ProfileNavItem[] {
  return PROFILE_NAV_ITEMS.filter((item) =>
    item.require ? item.require(account, user) : true,
  );
}

/** My Bets, Transactions, Bonuses — in that order. */
export function profileMenuPrimaryItems(
  account: Account | null | undefined,
  user?: UserSettings | null,
): ProfileNavItem[] {
  const byId = new Map(profileNavItems(account, user).map((item) => [item.id, item]));
  return PROFILE_MENU_PRIMARY_IDS.flatMap((id) => {
    const item = byId.get(id);
    return item ? [item] : [];
  });
}

/** Remaining gated items excluding the primary three (config order). */
export function profileMenuMoreItems(
  account: Account | null | undefined,
  user?: UserSettings | null,
): ProfileNavItem[] {
  const primary = new Set<ProfileNavId>(PROFILE_MENU_PRIMARY_IDS);
  return profileNavItems(account, user).filter((item) => !primary.has(item.id));
}
